import { uiText } from '@/i18n/interface';
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from "@/hooks/use-toast";
import { getCurrencySymbol } from '@/utils/currencyUtils';

export const useCurrencySettings = () => {
  const [defaultCurrency, setDefaultCurrency] = useState('USD');
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const fetchCurrencySettings = async () => {
    try {
      console.log('Fetching currency settings...');

      // Get current user's dojo payment categories to determine currency
      const { data: userProfile } = await supabase
        .from('profiles')
        .select('dojo_id')
        .eq('id', (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (userProfile?.dojo_id) {
        // Get currency from the dojo's payment categories (most recent first)
        const { data: paymentCategories } = await supabase
          .from('payment_categories')
          .select('currency')
          .eq('dojo_id', userProfile.dojo_id)
          .eq('is_active', true)
          .order('created_at', { ascending: false });

        if (paymentCategories && paymentCategories.length > 0 && paymentCategories[0].currency) {
          console.log('Setting dojo currency to:', paymentCategories[0].currency);
          setDefaultCurrency(paymentCategories[0].currency);
          setIsLoading(false);
          return;
        }
      }

      // Fallback to default CHF currency for new dojos
      console.log('No dojo currency found, using CHF default');
      setDefaultCurrency('CHF');
    } catch (error) {
      console.error('Error fetching currency settings:', error);
      // Always default to CHF for clean setup
      setDefaultCurrency('CHF');
    } finally {
      setIsLoading(false);
    }
  };

  const updateDefaultCurrency = async (newCurrency: string) => {
    try {
      // Get current user's dojo ID
      const { data: userProfile } = await supabase
        .from('profiles')
        .select('dojo_id')
        .eq('id', (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (!userProfile?.dojo_id) {
        throw new Error(uiText("User dojo not found"));
      }

      // Update currency for all payment categories in this dojo
      const { error: categoriesError } = await supabase
        .from('payment_categories')
        .update({ currency: newCurrency })
        .eq('dojo_id', userProfile.dojo_id);

      if (categoriesError) throw categoriesError;

      // Also update currency for all existing payments in this dojo
      const { error: paymentsError } = await supabase
        .from('payments')
        .update({ currency: newCurrency })
        .eq('dojo_id', userProfile.dojo_id);

      if (paymentsError) {
        console.warn('Error updating payment currencies:', paymentsError);
        // Don't throw error for payments update as categories are more important
      }

      setDefaultCurrency(newCurrency);

      toast({
        title: uiText("Currency Updated"),
        description: uiText("Dojo currency changed to {{value0}}. All payment categories and existing payments updated.", { value0: newCurrency })
      });

      // Force a page refresh to ensure all components get the new currency
      window.location.reload();

      return true;
    } catch (error) {
      console.error('Error updating currency:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to update dojo currency"),
        variant: "destructive"
      });
      return false;
    }
  };

  useEffect(() => {
    fetchCurrencySettings();
  }, []);

  const formatCurrency = (amount: number, currency?: string) => {
    const currencyCode = currency || defaultCurrency;
    const symbol = getCurrencySymbol(currencyCode);
    return `${symbol}${amount.toFixed(2)}`;
  };

  const updateDefaultCurrencyLocalStorage = (newCurrency: string) => {
    localStorage.setItem('defaultCurrency', newCurrency);
    setDefaultCurrency(newCurrency);
  };

  return {
    defaultCurrency,
    updateDefaultCurrency,
    formatCurrency,
    isLoading
  };
};